import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { useOrganizationsQuery } from './useOrganizationsQuery';
import { useOrganizationsSearch } from './useOrganizationsSearch';
import { Organization } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// ⚠️ `created` is NULLABLE on Organization (types.ts), which is weaker than the
// checklist's preference for an immutable NON-NULL key — a nullable key degrades
// to insertion order if EVERY row lacks it. There is no better option here: `id`
// and `adminAccountId` are the only non-null fields, and ordering organizations
// by an opaque id means nothing to an operator.
//
// `created` is still the right key. It is immutable, unlike `updated`, which
// would reorder the list under someone reading it. Rows missing it sort LAST,
// which is correct — a missing timestamp is a state, not the smallest value.
const DEFAULT_SORT_STATUS: DataTableSortStatus<Organization> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useOrganizations = (filters: Record<string, string> = {}) => {
    const query = useOrganizationsQuery(filters);
    const { keyword } = useOrganizationsSearch();

    // Type-guarded flatten: a page that failed or is still settling is not an
    // `{items}` shape, and a bare flatMap would throw on it.
    const items: Organization[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen, so the footer reports a LOADED count
    // rather than a total, and `unfilteredCount` travels alongside the match
    // count so an operator can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.name?.toLowerCase().includes(needle) ||
        item.description?.toLowerCase().includes(needle) ||
        item.email?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<Organization>(matched, DEFAULT_SORT_STATUS);

    // hasNextPage is checked HERE so no call site can fetch past the end.
    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
